import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  AutoComplete,
  Button,
  Card,
  Col,
  Form,
  Input,
  InputNumber,
  Radio,
  Row,
  Select,
  Space,
  Switch,
  Typography,
  message,
} from "antd";
import { ArrowLeftOutlined, SaveOutlined } from "@ant-design/icons";

import {
  purchasesApi,
  watchBrandsApi,
  perfumeBrandsApi,
} from "../services/api";
import type {
  PurchaseItemType,
  UserPurchaseCreate,
  UserPurchaseUpdate,
} from "../types";
import { usePermissions } from "../hooks/usePermissions";
import {
  GAME_FORMAT_LABELS,
  GAME_PLATFORM_OPTIONS,
  PURCHASE_TYPE_LABELS,
  isPurchaseItemType,
} from "./purchaseTypes";

const DETAILS_FIELDS = [
  "watch_details",
  "perfume_details",
  "game_details",
] as const;
const DETAILS_FIELD_BY_TYPE: Record<
  PurchaseItemType,
  (typeof DETAILS_FIELDS)[number]
> = {
  watch: "watch_details",
  perfume: "perfume_details",
  game: "game_details",
};

const { Title } = Typography;

export function PurchaseFormPage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { id } = useParams();
  const isEditing = Boolean(id);

  const [form] = Form.useForm();
  const { canCreate, canEdit } = usePermissions();
  const isReadOnly = isEditing ? !canEdit : !canCreate;
  const [searchParams] = useSearchParams();
  const requestedType = searchParams.get("item_type");
  const initialType: PurchaseItemType = isPurchaseItemType(requestedType)
    ? requestedType
    : "watch";
  const [itemType, setItemType] = useState<PurchaseItemType>(initialType);

  const { data: purchase, isLoading } = useQuery({
    queryKey: ["purchase", id],
    queryFn: () => purchasesApi.get(id as string),
    enabled: isEditing,
  });

  // Fetch watch brands
  const { data: watchBrandsData } = useQuery({
    queryKey: ["watch-brands"],
    queryFn: () => watchBrandsApi.list({ page_size: 1000 }),
  });

  // Fetch perfume brands
  const { data: perfumeBrandsData } = useQuery({
    queryKey: ["perfume-brands"],
    queryFn: () => perfumeBrandsApi.list({ page_size: 1000 }),
  });

  useEffect(() => {
    if (!purchase) return;

    setItemType(purchase.item_type);

    form.setFieldsValue({
      item_type: purchase.item_type,
      purchased_at: purchase.purchased_at,
      price: purchase.price,
      currency_code: purchase.currency_code,
      vendor_name: purchase.vendor_name,
      vendor_url: purchase.vendor_url,
      order_number: purchase.order_number,
      condition: purchase.condition,
      is_gift: purchase.is_gift,
      notes: purchase.notes,
      watch_details: purchase.watch_details || {},
      perfume_details: purchase.perfume_details || {},
      game_details: purchase.game_details || {},
    });
  }, [purchase, form]);

  const createMutation = useMutation({
    mutationFn: (payload: UserPurchaseCreate) => purchasesApi.create(payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["purchases"] });
      message.success("Zakup utworzony");
      navigate("/purchases");
    },
    onError: (error: any) => {
      const errorMessage =
        error?.response?.data?.detail || "Nie udało się utworzyć zakupu";
      message.error(errorMessage);
    },
  });

  const updateMutation = useMutation({
    mutationFn: (payload: UserPurchaseUpdate) =>
      purchasesApi.update(id as string, payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["purchases"] });
      queryClient.invalidateQueries({ queryKey: ["purchase", id] });
      message.success("Zakup zaktualizowany");
      navigate("/purchases");
    },
    onError: (error: any) => {
      const errorMessage =
        error?.response?.data?.detail || "Nie udało się zaktualizować zakupu";
      message.error(errorMessage);
    },
  });

  const title = useMemo(
    () => (isEditing ? "Edytuj zakup" : "Dodaj zakup"),
    [isEditing],
  );

  return (
    <div>
      <div style={{ marginBottom: 24 }}>
        <Button
          type="link"
          icon={<ArrowLeftOutlined />}
          onClick={() => navigate(-1)}
          style={{ padding: 0 }}
        >
          Powrót
        </Button>
        <Title level={2} style={{ marginTop: 8, marginBottom: 0 }}>
          {title}
        </Title>
      </div>

      <Card loading={isEditing && isLoading}>
        <Form
          form={form}
          layout="vertical"
          initialValues={{
            item_type: initialType,
            purchased_at: new Date().toISOString().slice(0, 10),
            currency_code: "PLN",
            is_gift: false,
          }}
          onFinish={(values) => {
            const base = {
              item_type: values.item_type as PurchaseItemType,
              purchased_at: values.purchased_at as string,
              price: values.price as number | undefined,
              currency_code: values.currency_code as string,
              vendor_name: values.vendor_name as string | undefined,
              vendor_url: values.vendor_url as string | undefined,
              order_number: values.order_number as string | undefined,
              condition: values.condition as string | undefined,
              is_gift: values.is_gift as boolean,
              notes: values.notes as string | undefined,
            };

            // Send only the details block matching the chosen type
            const detailsField = DETAILS_FIELD_BY_TYPE[base.item_type];
            const payload: UserPurchaseCreate | UserPurchaseUpdate = {
              ...base,
              [detailsField]: values[detailsField] || {},
            };
            if (isEditing) updateMutation.mutate(payload as UserPurchaseUpdate);
            else createMutation.mutate(payload as UserPurchaseCreate);
          }}
        >
          <Form.Item
            label="Rodzaj"
            name="item_type"
            rules={[{ required: true, message: "Wybierz rodzaj" }]}
          >
            <Radio.Group
              optionType="button"
              buttonStyle="solid"
              value={itemType}
              onChange={(e) => {
                const v = e.target.value as PurchaseItemType;
                setItemType(v);
                form.setFieldValue("item_type", v);
                DETAILS_FIELDS.forEach((f) => form.setFieldValue(f, {}));
              }}
              options={(
                Object.keys(PURCHASE_TYPE_LABELS) as PurchaseItemType[]
              ).map((t) => ({
                label: PURCHASE_TYPE_LABELS[t].singular,
                value: t,
              }))}
            />
          </Form.Item>

          <Row gutter={16}>
            <Col xs={24} md={8}>
              <Form.Item
                name="purchased_at"
                label="Data zakupu"
                rules={[{ required: true, message: "Data jest wymagana" }]}
              >
                <Input type="date" />
              </Form.Item>
            </Col>
            <Col xs={24} md={8}>
              <Form.Item name="price" label="Cena">
                <InputNumber style={{ width: "100%" }} min={0} step={0.01} />
              </Form.Item>
            </Col>
            <Col xs={24} md={8}>
              <Form.Item
                name="currency_code"
                label="Waluta"
                rules={[{ required: true, message: "Waluta jest wymagana" }]}
              >
                <Input placeholder="PLN" />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col xs={24} md={8}>
              <Form.Item name="vendor_name" label="Sklep / sprzedawca">
                <Input />
              </Form.Item>
            </Col>
            <Col xs={24} md={8}>
              <Form.Item name="order_number" label="Numer zamówienia">
                <Input />
              </Form.Item>
            </Col>
            <Col xs={24} md={8}>
              <Form.Item name="vendor_url" label="Link do oferty / sklepu">
                <Input />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col xs={24} md={8}>
              <Form.Item name="condition" label="Stan">
                <Input />
              </Form.Item>
            </Col>
            <Col xs={24} md={8}>
              <Form.Item name="is_gift" label="Prezent" valuePropName="checked">
                <Switch />
              </Form.Item>
            </Col>
          </Row>

          {itemType === "game" && (
            <>
              <Title level={4} style={{ marginTop: 8 }}>
                Szczegóły gry
              </Title>
              <Row gutter={16}>
                <Col xs={24} md={12}>
                  <Form.Item
                    name={["game_details", "title"]}
                    label="Tytuł"
                    rules={[{ required: true, message: "Podaj tytuł gry" }]}
                  >
                    <Input />
                  </Form.Item>
                </Col>
                <Col xs={24} md={12}>
                  <Form.Item
                    name={["game_details", "platform"]}
                    label="Platforma"
                  >
                    <AutoComplete
                      options={GAME_PLATFORM_OPTIONS}
                      placeholder="np. PC, PlayStation 5"
                      filterOption={(input, option) =>
                        (option?.value as string)
                          .toLowerCase()
                          .includes(input.toLowerCase())
                      }
                    />
                  </Form.Item>
                </Col>
              </Row>

              <Row gutter={16}>
                <Col xs={24} md={8}>
                  <Form.Item name={["game_details", "format"]} label="Wersja">
                    <Radio.Group
                      optionType="button"
                      options={Object.entries(GAME_FORMAT_LABELS).map(
                        ([value, label]) => ({ value, label }),
                      )}
                    />
                  </Form.Item>
                </Col>
                <Col xs={24} md={8}>
                  <Form.Item name={["game_details", "edition"]} label="Edycja">
                    <Input placeholder="np. Standard, Deluxe, GOTY" />
                  </Form.Item>
                </Col>
                <Col xs={24} md={8}>
                  <Form.Item
                    name={["game_details", "is_dlc"]}
                    label="DLC / dodatek"
                    valuePropName="checked"
                  >
                    <Switch />
                  </Form.Item>
                </Col>
              </Row>
            </>
          )}

          {itemType === "watch" && (
            <>
              <Title level={4} style={{ marginTop: 8 }}>
                Szczegóły zegarka
              </Title>
              <Row gutter={16}>
                <Col xs={24} md={8}>
                  <Form.Item name={["watch_details", "brand_id"]} label="Marka">
                    <Select
                      showSearch
                      placeholder="Wybierz markę"
                      optionFilterProp="children"
                      filterOption={(input, option) =>
                        (option?.children as unknown as string)
                          ?.toLowerCase()
                          .includes(input.toLowerCase())
                      }
                    >
                      {watchBrandsData?.items.map((brand) => (
                        <Select.Option key={brand.id} value={brand.id}>
                          {brand.name}
                        </Select.Option>
                      ))}
                    </Select>
                  </Form.Item>
                </Col>
                <Col xs={24} md={8}>
                  <Form.Item name={["watch_details", "model"]} label="Model">
                    <Input />
                  </Form.Item>
                </Col>
                <Col xs={24} md={8}>
                  <Form.Item
                    name={["watch_details", "reference_number"]}
                    label="Referencja"
                  >
                    <Input />
                  </Form.Item>
                </Col>
              </Row>

              <Row gutter={16}>
                <Col xs={24} md={8}>
                  <Form.Item
                    name={["watch_details", "serial_number"]}
                    label="Numer seryjny"
                  >
                    <Input />
                  </Form.Item>
                </Col>
                <Col xs={24} md={8}>
                  <Form.Item
                    name={["watch_details", "movement_type"]}
                    label="Mechanizm"
                  >
                    <Input />
                  </Form.Item>
                </Col>
                <Col xs={24} md={8}>
                  <Form.Item
                    name={["watch_details", "case_material"]}
                    label="Materiał koperty"
                  >
                    <Input />
                  </Form.Item>
                </Col>
              </Row>

              <Row gutter={16}>
                <Col xs={24} md={8}>
                  <Form.Item
                    name={["watch_details", "case_size_mm"]}
                    label="Rozmiar koperty (mm)"
                  >
                    <InputNumber style={{ width: "100%" }} min={0} step={0.1} />
                  </Form.Item>
                </Col>
                <Col xs={24} md={8}>
                  <Form.Item
                    name={["watch_details", "water_resistance_m"]}
                    label="WR (m)"
                  >
                    <InputNumber style={{ width: "100%" }} min={0} step={10} />
                  </Form.Item>
                </Col>
                <Col xs={24} md={8}>
                  <Form.Item
                    name={["watch_details", "dial_color"]}
                    label="Kolor tarczy"
                  >
                    <Input />
                  </Form.Item>
                </Col>
              </Row>

              <Row gutter={16}>
                <Col xs={24} md={8}>
                  <Form.Item
                    name={["watch_details", "strap_material"]}
                    label="Pasek / bransoleta"
                  >
                    <Input />
                  </Form.Item>
                </Col>
                <Col xs={24} md={8}>
                  <Form.Item
                    name={["watch_details", "production_year"]}
                    label="Rok produkcji"
                  >
                    <InputNumber
                      style={{ width: "100%" }}
                      min={1900}
                      max={2100}
                    />
                  </Form.Item>
                </Col>
                <Col xs={24} md={8}>
                  <Form.Item
                    name={["watch_details", "warranty_until"]}
                    label="Gwarancja do"
                  >
                    <Input type="date" />
                  </Form.Item>
                </Col>
              </Row>

              <Row gutter={16}>
                <Col xs={24} md={8}>
                  <Form.Item
                    name={["watch_details", "last_service_at"]}
                    label="Ostatni serwis"
                  >
                    <Input type="date" />
                  </Form.Item>
                </Col>
              </Row>
            </>
          )}

          {itemType === "perfume" && (
            <>
              <Title level={4} style={{ marginTop: 8 }}>
                Szczegóły perfum
              </Title>
              <Row gutter={16}>
                <Col xs={24} md={8}>
                  <Form.Item
                    name={["perfume_details", "brand_id"]}
                    label="Marka / dom perfumeryjny"
                  >
                    <Select
                      showSearch
                      placeholder="Wybierz markę"
                      optionFilterProp="children"
                      filterOption={(input, option) =>
                        (option?.children as unknown as string)
                          ?.toLowerCase()
                          .includes(input.toLowerCase())
                      }
                    >
                      {perfumeBrandsData?.items.map((brand) => (
                        <Select.Option key={brand.id} value={brand.id}>
                          {brand.name}
                        </Select.Option>
                      ))}
                    </Select>
                  </Form.Item>
                </Col>
                <Col xs={24} md={8}>
                  <Form.Item
                    name={["perfume_details", "fragrance_name"]}
                    label="Nazwa zapachu"
                  >
                    <Input />
                  </Form.Item>
                </Col>
                <Col xs={24} md={8}>
                  <Form.Item
                    name={["perfume_details", "concentration"]}
                    label="Koncentracja"
                  >
                    <Select placeholder="Wybierz koncentrację">
                      <Select.Option value="parfum">
                        Parfum (Extrait)
                      </Select.Option>
                      <Select.Option value="edp">
                        EDP (Eau de Parfum)
                      </Select.Option>
                      <Select.Option value="edt">
                        EDT (Eau de Toilette)
                      </Select.Option>
                      <Select.Option value="edc">
                        EDC (Eau de Cologne)
                      </Select.Option>
                      <Select.Option value="edf">
                        EDF (Eau de Fraîcheur)
                      </Select.Option>
                      <Select.Option value="bm">Body Mist</Select.Option>
                    </Select>
                  </Form.Item>
                </Col>
              </Row>

              <Row gutter={16}>
                <Col xs={24} md={8}>
                  <Form.Item
                    name={["perfume_details", "size_ml"]}
                    label="Pojemność (ml)"
                  >
                    <InputNumber style={{ width: "100%" }} min={0} step={1} />
                  </Form.Item>
                </Col>
                <Col xs={24} md={8}>
                  <Form.Item
                    name={["perfume_details", "batch_code"]}
                    label="Batch code"
                  >
                    <Input />
                  </Form.Item>
                </Col>
                <Col xs={24} md={8}>
                  <Form.Item
                    name={["perfume_details", "opened_at"]}
                    label="Otwarte dnia"
                  >
                    <Input type="date" />
                  </Form.Item>
                </Col>
              </Row>
            </>
          )}

          <Form.Item name="notes" label="Notatki">
            <Input.TextArea rows={4} />
          </Form.Item>

          <Form.Item
            style={{ marginTop: 24, marginBottom: 0, textAlign: "right" }}
          >
            <Space>
              <Button onClick={() => navigate(-1)}>Anuluj</Button>
              <Button
                type="primary"
                htmlType="submit"
                icon={<SaveOutlined />}
                loading={createMutation.isPending || updateMutation.isPending}
                disabled={isReadOnly}
                title={isReadOnly ? "Brak uprawnień do edycji" : undefined}
              >
                Zapisz
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Card>
    </div>
  );
}

export default PurchaseFormPage;
